// Eigenbau: sichere Projektansicht für Durchsicht-Läufe und Befunde (Paket 12)
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { useCursorListe } from "@/lib/haken";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Leerzustand } from "@/components/Leerzustand";
import { MehrLaden } from "@/components/MehrLaden";

export function DurchsichtReiter({
  projektId,
  nurOffen = false,
}: {
  projektId: string;
  nurOffen?: boolean;
}) {
  const laeufe = useCursorListe({
    queryKey: schluessel.durchsichtLaeufe(projektId),
    lade: (cursor) =>
      api.durchsicht.laeufe.query({ projektId, cursor, grenze: 50 }),
  });
  const befunde = useCursorListe({
    queryKey: schluessel.durchsichtBefunde(projektId),
    lade: (cursor) =>
      api.durchsicht.befunde.query({ projektId, cursor, grenze: 50 }),
  });
  const sichtbareBefunde = befunde.eintraege.filter(
    (befund) => !nurOffen || befund.zustand === "offen",
  );

  return (
    <div className="space-y-8" data-testid="projekt-durchsicht">
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          Läufe
        </h3>
        {laeufe.isLoading ? (
          <p className="mt-2 text-sm text-gray-500">Lade Läufe …</p>
        ) : laeufe.isError ? (
          <div className="mt-2 space-y-2" role="alert">
            <p className="text-sm text-red-600">
              Durchsichtsläufe konnten nicht geladen werden.
            </p>
            <Button variant="secondary" onClick={() => void laeufe.refetch()}>
              Erneut versuchen
            </Button>
          </div>
        ) : laeufe.eintraege.length === 0 ? (
          <Leerzustand
            titel="Noch keine Durchsichtläufe."
            hinweis="Läufe erscheinen hier, sobald eine Code-Durchsicht für dieses Projekt angefordert wurde."
          />
        ) : (
          <ul className="mt-2 space-y-2">
            {laeufe.eintraege.map((lauf) => (
              <li
                key={lauf.id}
                className="rounded-md border border-gray-200 p-3 text-sm dark:border-gray-800"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>{lauf.zustand}</Badge>
                  <span>{lauf.modell || "Modell nicht angegeben"}</span>
                  <span className="text-gray-500">
                    {lauf.dauerMs === null
                      ? "keine Dauer"
                      : `${lauf.dauerMs} ms`}
                  </span>
                  <span className="text-gray-500">
                    {lauf.versuche} Versuch(e)
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <MehrLaden
          hatMehr={laeufe.hasNextPage}
          laedt={laeufe.isFetchingNextPage}
          laden={() => void laeufe.fetchNextPage()}
          beschriftung="Ältere Läufe laden"
        />
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {nurOffen ? "Offene Befunde" : "Befunde"}
        </h3>
        {befunde.isLoading ? (
          <p className="mt-2 text-sm text-gray-500">Lade Befunde …</p>
        ) : befunde.isError ? (
          <div className="mt-2 space-y-2" role="alert">
            <p className="text-sm text-red-600">
              Befunde konnten nicht geladen werden.
            </p>
            <Button variant="secondary" onClick={() => void befunde.refetch()}>
              Erneut versuchen
            </Button>
          </div>
        ) : sichtbareBefunde.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">
            {nurOffen
              ? "Keine offenen Befunde in diesem Projekt."
              : "Keine Befunde in diesem Projekt."}
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {sichtbareBefunde.map((befund) => (
              <li
                key={befund.id}
                className="rounded-md border border-gray-200 p-3 dark:border-gray-800"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>{befund.schwere}</Badge>
                  <Badge variant="neutral">{befund.zustand}</Badge>
                  <span className="font-mono text-xs text-gray-500">
                    {befund.datei}
                    {befund.zeile ? `:${befund.zeile}` : ""}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-900 dark:text-gray-50">
                  {befund.text}
                </p>
              </li>
            ))}
          </ul>
        )}
        <MehrLaden
          hatMehr={befunde.hasNextPage}
          laedt={befunde.isFetchingNextPage}
          laden={() => void befunde.fetchNextPage()}
          beschriftung="Weitere Befunde laden"
        />
      </section>
    </div>
  );
}
